/**
 * Shifts going spare: ones a colleague cannot work, and ones nobody was ever
 * put on. Staff offer to take them; the boss decides who actually gets it.
 *
 * Deliberately an OFFER and not a booking. Two people can put their hand up for
 * the same shift without either of them being told they have it, because the
 * boss carries the wage cost and the responsibility for who is on the floor.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Hand, CalendarClock, Check } from 'lucide-react';
import { api, ApiError, qk, type AvailableShift } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import { EmptyState, ErrorState, LoadingBlock, Spinner } from '../../components/Bits.tsx';
import { OutcomeModal } from '../../components/Overlay.tsx';
import { fmtDateLong, fmtDuration, fmtTimeRange, shiftMinutes } from '../../lib/time.ts';

export default function CoverPage() {
  const { tenant } = useSession();
  const slug = tenant?.tenantSlug ?? '';
  const qc = useQueryClient();
  const [outcome, setOutcome] = useState<string | null>(null);

  const query = useQuery({
    queryKey: qk.availableShifts(slug),
    queryFn: () => api.availableShifts(slug),
    enabled: Boolean(slug),
  });

  const claim = useMutation({
    mutationFn: (s: AvailableShift) =>
      s.claimedByMe ? api.unclaimShift(slug, s.id) : api.claimShift(slug, s.id),
    onSuccess: (_r, s) => {
      void qc.invalidateQueries({ queryKey: qk.availableShifts(slug) });
      setOutcome(
        s.claimedByMe
          ? 'You have taken your name off that one.'
          : 'Your manager has been told you can work it. They will confirm before it is yours, so do not count on it yet.',
      );
    },
    onError: (e) =>
      setOutcome(e instanceof ApiError ? e.message : 'Could not send that. Try again in a moment.'),
  });

  if (!slug) return null;
  if (query.isLoading) return <LoadingBlock label="Loading shifts" />;
  if (query.isError) {
    return <ErrorState error={query.error} what="the available shifts" onRetry={() => void query.refetch()} />;
  }

  const items = query.data?.items ?? [];

  return (
    <div className="px-4 py-4">
      <h1 className="text-[20px] font-extrabold tracking-tight text-ink">Up for grabs</h1>
      <p className="mt-1 text-[13px] leading-relaxed text-muted">
        Shifts nobody is covering yet. Put your hand up and your manager decides.
      </p>

      {items.length === 0 ? (
        <div className="mt-5">
          <EmptyState
            title="Nothing going spare"
            body="When somebody cannot work a shift, or a shift has nobody on it, it turns up here."
          />
        </div>
      ) : (
        <ul className="mt-4 grid gap-2.5">
          {items.map((s, i) => (
            <li key={s.id} className="card card-lift anim-rise p-3.5" style={{ animationDelay: `${i * 45}ms` }}>
              <div className="flex items-start gap-3">
                <span
                  className="flex h-9 w-9 shrink-0 items-center justify-center"
                  style={{
                    borderRadius: 'var(--sp-radius)',
                    background: 'var(--sp-warn-soft)',
                    color: 'var(--sp-warn-strong)',
                  }}
                  aria-hidden="true"
                >
                  <CalendarClock size={17} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-[14px] font-bold text-ink">{fmtDateLong(s.date)}</p>
                  <p className="tabular text-[13px] text-muted">
                    {fmtTimeRange(s.startTime, s.endTime)}
                    {' · '}
                    {fmtDuration(shiftMinutes(s.startTime, s.endTime, s.breakMinutes))}
                  </p>
                  {s.reason ? (
                    <p className="mt-1 text-[12.5px] text-faint">Reason given: {s.reason}</p>
                  ) : null}
                  {s.open ? (
                    <span className="chip chip-neutral mt-2">Nobody rostered</span>
                  ) : (
                    <span className="chip chip-warn mt-2">Needs cover</span>
                  )}
                </div>
              </div>
              <button
                type="button"
                className={`btn btn-lg mt-3 w-full ${s.claimedByMe ? '' : 'btn-primary'}`}
                onClick={() => claim.mutate(s)}
                disabled={claim.isPending}
              >
                {claim.isPending ? (
                  <Spinner size={15} label="Sending" />
                ) : s.claimedByMe ? (
                  <>
                    <Check size={15} aria-hidden="true" />
                    You offered. Tap to withdraw
                  </>
                ) : (
                  <>
                    <Hand size={15} aria-hidden="true" />
                    I can work this
                  </>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}

      <OutcomeModal
        open={outcome !== null}
        onClose={() => setOutcome(null)}
        ok
        title="Thanks"
        message={outcome ?? ''}
      />
    </div>
  );
}
